/**
 * BOINCLO SLIDE LAZY LOADER
 * -------------------------
 * Loads product card slideshow images on-demand — only when the user
 * actually swipes to that slide. This prevents 70-100+ simultaneous
 * image requests on collection pages, which causes the broken image
 * (question mark) bug on mobile.
 *
 * How it works:
 * - Non-first slide images are rendered with src="data:..." (a 1x1 transparent SVG)
 *   and their real URL stored in data-src / data-srcset.
 * - This script watches each slider for scroll events and swaps data-src → src
 *   for any .boinclo-slide-lazy image that is about to become visible.
 * - It also uses IntersectionObserver to pre-load the next slide's image
 *   one slide ahead, so there's no visible delay when swiping.
 */

(function () {
  'use strict';

  /**
   * Activates a lazy image by moving data-src → src and data-srcset → srcset.
   * @param {HTMLImageElement} img
   */
  function activateLazyImage(img) {
    if (!img || img.dataset.activated) return;
    img.dataset.activated = '1';

    const src = img.dataset.src;
    const srcset = img.dataset.srcset;
    const sizes = img.dataset.sizes;

    if (srcset) img.srcset = srcset;
    if (sizes) img.sizes = sizes;
    if (src) img.src = src;

    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-sizes');
    img.classList.remove('boinclo-slide-lazy');
  }

  /**
   * Given a slider element, pre-loads the image in the slide immediately
   * after the currently visible one.
   * @param {HTMLElement} sliderEl  The .slider element (the scrollable container)
   */
  function preloadNextSlide(sliderEl) {
    const slides = Array.from(sliderEl.querySelectorAll('.slider__item:not([hidden])'));
    if (!slides.length) return;

    // Determine which slide is currently in view
    const scrollLeft = sliderEl.scrollLeft;
    const slideWidth = sliderEl.offsetWidth;
    const currentIndex = Math.round(scrollLeft / slideWidth);

    // Activate current + next slide images
    [currentIndex, currentIndex + 1].forEach(function (idx) {
      if (idx < slides.length) {
        const lazyImg = slides[idx].querySelector('.boinclo-slide-lazy');
        if (lazyImg) activateLazyImage(lazyImg);
      }
    });
  }

  /**
   * Attaches scroll listener to a product-card-image-slider element.
   * @param {HTMLElement} cardSlider
   */
  function attachSlider(cardSlider) {
    const sliderEl = cardSlider.querySelector('.slider');
    if (!sliderEl) return;

    // Pre-load slide 2 immediately (index 1) so first swipe is instant
    const secondSlide = cardSlider.querySelector('.slider__item:nth-child(2)');
    if (secondSlide) {
      const lazyImg = secondSlide.querySelector('.boinclo-slide-lazy');
      if (lazyImg) {
        // Use requestIdleCallback so it doesn't compete with first-image loading
        if ('requestIdleCallback' in window) {
          requestIdleCallback(function () { activateLazyImage(lazyImg); }, { timeout: 2000 });
        } else {
          setTimeout(function () { activateLazyImage(lazyImg); }, 800);
        }
      }
    }

    // On scroll (swipe), load current + next
    sliderEl.addEventListener('scroll', function () {
      preloadNextSlide(sliderEl);
    }, { passive: true });
  }

  /**
   * Uses IntersectionObserver to only attach the scroll listener once a
   * product card is near the viewport — avoids wasting memory on 24 listeners
   * that are all set up at once.
   */
  function initObserver() {
    if (!('IntersectionObserver' in window)) {
      // Fallback: just activate all immediately
      document.querySelectorAll('.boinclo-slide-lazy').forEach(activateLazyImage);
      return;
    }

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          attachSlider(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '200px 0px', // start loading 200px before card enters viewport
      threshold: 0
    });

    document.querySelectorAll('product-card-image-slider').forEach(function (el) {
      observer.observe(el);
    });
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initObserver);
  } else {
    initObserver();
  }

  // Also handle dynamically added cards (infinite scroll / AJAX pagination)
  const mutationObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        const sliders = node.querySelectorAll
          ? node.querySelectorAll('product-card-image-slider')
          : [];
        sliders.forEach(function (el) {
          attachSlider(el);
        });
        if (node.tagName === 'PRODUCT-CARD-IMAGE-SLIDER') {
          attachSlider(node);
        }
      });
    });
  });

  mutationObserver.observe(document.body, { childList: true, subtree: true });

})();